import type { RemittanceDraftRow } from '../components/RemittanceEditTable';
import { computeSanjeAndRemittance } from './remittanceAmounts';
import { withdrawalAccountMemoFrom } from './remittanceWithdrawalMemo';

/** 차량번호(공백 제외) 기준 그룹 키 — 차량번호 없으면 행마다 분리 */
function vehicleGroupKey(row: RemittanceDraftRow): string {
  const vn = String(row.vehicleNumber ?? '').trim();
  return vn || `__nov_${row.sourceId}`;
}

/**
 * 같은 차량번호가 여러 줄일 때 송금 모달·대량이체용으로 1행으로 통합
 * - 지불운송비 합산 후 산재·송금액 재계산(동일 차량 다건)
 * - 운송구간: 첫 행 구간 + " 외 N건" (N = 전체 건수 - 1)
 */
export function mergeRemittanceDraftByVehicle(rows: RemittanceDraftRow[]): RemittanceDraftRow[] {
  if (rows.length === 0) return [];

  const groups = new Map<string, RemittanceDraftRow[]>();
  const keyOrder: string[] = [];

  for (const row of rows) {
    const key = vehicleGroupKey(row);
    if (!groups.has(key)) {
      groups.set(key, []);
      keyOrder.push(key);
    }
    groups.get(key)!.push(row);
  }

  return keyOrder.map(key => {
    const group = groups.get(key)!;
    if (group.length === 1) return group[0];

    const first = group[0];
    const totalPay = group.reduce((sum, r) => sum + (Number(r.paymentAmount) || 0), 0);
    const bizNoMerged = String(first.colE ?? '').trim();
    const { sanje, remittance } = computeSanjeAndRemittance(totalPay, bizNoMerged);
    const vn = String(first.vehicleNumber ?? '').trim();

    const firstRoute = String(first.route ?? '').trim();
    const n = group.length;
    let routeLabel: string;
    if (n === 1) {
      routeLabel = firstRoute;
    } else if (firstRoute) {
      routeLabel = `${firstRoute} 외 ${n - 1}건`;
    } else {
      routeLabel = `${n}건`;
    }

    const biz = String(first.businessName ?? '').trim();
    return {
      ...first,
      sourceId: vn ? `merged-${vn}` : `merged-${first.sourceId}`,
      vehicleNumber: vn,
      route: routeLabel,
      paymentAmount: totalPay,
      colC: remittance,
      colSanje: sanje,
      colF: withdrawalAccountMemoFrom(biz, vn),
    };
  });
}
